// Synthetic player data generator for Virginia Baseball roster

// Helper function to generate realistic variations
const vary = (base, variance) => {
  const multiplier = 1 + (Math.random() - 0.5) * variance;
  return Math.round(base * multiplier);
};

// Player archetypes
const archetypes = {
  star: {
    slamScoreRange: [80, 95],
    nilValueRange: [15000, 35000],
    socialFollowersRange: [8000, 20000],
    engagementRange: [6, 12],
    growthRate: 'high'
  },
  rising: {
    slamScoreRange: [70, 85],
    nilValueRange: [8000, 18000],
    socialFollowersRange: [3000, 10000],
    engagementRange: [4, 8],
    growthRate: 'medium'
  },
  solid: {
    slamScoreRange: [65, 78],
    nilValueRange: [5000, 12000],
    socialFollowersRange: [1500, 5000],
    engagementRange: [3, 6],
    growthRate: 'low'
  },
  developing: {
    slamScoreRange: [55, 70],
    nilValueRange: [2000, 8000],
    socialFollowersRange: [500, 3000],
    engagementRange: [2, 5],
    growthRate: 'high'
  }
};

// Brand partnership templates
const brandTemplates = {
  national: [
    { name: 'Nike', valueRange: [5000, 12000], probability: [50, 85] },
    { name: 'Adidas', valueRange: [4500, 10000], probability: [45, 80] },
    { name: 'Gatorade', valueRange: [3500, 8000], probability: [60, 85] },
    { name: 'Under Armour', valueRange: [4000, 9000], probability: [55, 80] },
    { name: 'New Balance', valueRange: [3000, 7000], probability: [50, 75] }
  ],
  midMarket: [
    { name: 'Barstool Sports', valueRange: [2500, 5000], probability: [70, 90] },
    { name: 'Liquid I.V.', valueRange: [2000, 4500], probability: [65, 85] },
    { name: 'BodyArmor', valueRange: [2200, 4000], probability: [60, 80] },
    { name: 'Athletic Greens', valueRange: [1800, 3500], probability: [55, 75] },
    { name: 'Hyperice', valueRange: [1500, 3000], probability: [50, 70] }
  ],
  local: [
    { name: 'Charlottesville Auto Group', valueRange: [1500, 3000], probability: [80, 95] },
    { name: "Miller's Sports Grill", valueRange: [800, 1800], probability: [85, 95] },
    { name: 'University Bookstore', valueRange: [1000, 2000], probability: [90, 98] },
    { name: 'Local Fitness Center', valueRange: [600, 1500], probability: [75, 90] },
    { name: 'Regional Bank', valueRange: [1200, 2500], probability: [70, 85] }
  ]
};

// Generate brand partnerships based on player archetype
const generateBrandPartnerships = (archetype, slamScore) => {
  const partnerships = [];
  
  // National brands (for star/rising players)
  if (archetype === 'star' || (archetype === 'rising' && slamScore > 80)) {
    const numNational = Math.floor(Math.random() * 2) + 1;
    for (let i = 0; i < numNational; i++) {
      const brand = brandTemplates.national[Math.floor(Math.random() * brandTemplates.national.length)];
      partnerships.push({
        brand: brand.name,
        tier: 'National',
        potentialValue: vary(
          (brand.valueRange[0] + brand.valueRange[1]) / 2,
          0.3
        ),
        requirements: `SLAM ${slamScore - 5}+, ${Math.round(slamScore * 120)}+ social`,
        probability: vary(
          (brand.probability[0] + brand.probability[1]) / 2,
          0.2
        ),
        timeline: '3-6 months'
      });
    }
  }
  
  // Mid-market brands
  const numMidMarket = archetype === 'star' ? 2 : archetype === 'rising' ? 2 : 1;
  for (let i = 0; i < numMidMarket; i++) {
    const brand = brandTemplates.midMarket[Math.floor(Math.random() * brandTemplates.midMarket.length)];
    partnerships.push({
      brand: brand.name,
      tier: 'Mid-Market',
      potentialValue: vary(
        (brand.valueRange[0] + brand.valueRange[1]) / 2,
        0.25
      ),
      requirements: 'Strong social presence',
      probability: vary(
        (brand.probability[0] + brand.probability[1]) / 2,
        0.15
      ),
      timeline: '1-3 months'
    });
  }
  
  // Local brands (everyone gets these)
  const numLocal = archetype === 'developing' ? 3 : 2;
  for (let i = 0; i < numLocal; i++) {
    const brand = brandTemplates.local[Math.floor(Math.random() * brandTemplates.local.length)];
    partnerships.push({
      brand: brand.name,
      tier: 'Local',
      potentialValue: vary(
        (brand.valueRange[0] + brand.valueRange[1]) / 2,
        0.2
      ),
      requirements: 'Community involvement',
      probability: vary(
        (brand.probability[0] + brand.probability[1]) / 2,
        0.1
      ),
      timeline: 'Immediate'
    });
  }
  
  return partnerships;
};

// Generate performance metrics
const generatePerformanceMetrics = (position, archetype) => {
  const baseMetrics = {
    P: { // Pitcher
      velocity: { base: 88, variance: 0.1 },
      spinRate: { base: 2200, variance: 0.15 },
      control: { base: 75, variance: 0.2 },
      stamina: { base: 80, variance: 0.15 }
    },
    C: { // Catcher
      popTime: { base: 1.95, variance: 0.05 },
      blocking: { base: 85, variance: 0.15 },
      framing: { base: 78, variance: 0.2 },
      armStrength: { base: 82, variance: 0.1 }
    },
    IF: { // Infielder
      fieldingPct: { base: 0.975, variance: 0.02 },
      range: { base: 82, variance: 0.15 },
      armStrength: { base: 85, variance: 0.1 },
      reaction: { base: 88, variance: 0.12 }
    },
    OF: { // Outfielder
      speed: { base: 85, variance: 0.15 },
      armStrength: { base: 88, variance: 0.1 },
      route: { base: 80, variance: 0.2 },
      range: { base: 90, variance: 0.1 }
    }
  };
  
  // Adjust based on archetype
  const archetypeMultiplier = {
    star: 1.15,
    rising: 1.05,
    solid: 1.0,
    developing: 0.9
  };
  
  const positionCategory = position === 'P' ? 'P' : 
                          position === 'C' ? 'C' : 
                          ['1B', '2B', '3B', 'SS'].includes(position) ? 'IF' : 'OF';
  
  const metrics = {};
  const basePositionMetrics = baseMetrics[positionCategory];
  
  Object.entries(basePositionMetrics).forEach(([metric, config]) => {
    const adjustedBase = config.base * archetypeMultiplier[archetype];
    metrics[metric] = {
      current: vary(adjustedBase, config.variance),
      projected: (months) => {
        const growthRate = archetype === 'star' ? 0.5 : 
                          archetype === 'rising' ? 0.8 : 
                          archetype === 'solid' ? 0.3 : 1.0;
        return Math.min(
          vary(adjustedBase, config.variance) + months * growthRate,
          config.base * 1.2
        );
      }
    };
  });
  
  return metrics;
};

// Generate social media data
const generateSocialMedia = (totalFollowers, archetype) => {
  const distribution = {
    instagram: 0.35 + Math.random() * 0.1,
    twitter: 0.25 + Math.random() * 0.1,
    tiktok: 0.3 + Math.random() * 0.1
  };
  
  // Normalize
  const total = distribution.instagram + distribution.twitter + distribution.tiktok;
  Object.keys(distribution).forEach(key => {
    distribution[key] = distribution[key] / total;
  });
  
  const engagementBase = archetypes[archetype].engagementRange;
  
  return {
    instagram: {
      followers: Math.round(totalFollowers * distribution.instagram),
      rate: 15 + Math.random() * 10,
      engagement: engagementBase[0] + Math.random() * (engagementBase[1] - engagementBase[0])
    },
    twitter: {
      followers: Math.round(totalFollowers * distribution.twitter),
      rate: 12 + Math.random() * 8,
      engagement: (engagementBase[0] + Math.random() * (engagementBase[1] - engagementBase[0])) * 0.7
    },
    tiktok: {
      followers: Math.round(totalFollowers * distribution.tiktok),
      rate: 10 + Math.random() * 15,
      engagement: (engagementBase[0] + Math.random() * (engagementBase[1] - engagementBase[0])) * 1.5
    }
  };
};

// Generate performance multipliers
const generateMultipliers = (archetype, year, gpa) => {
  const multipliers = [];
  
  // Academic
  multipliers.push({
    factor: 'Academic Standing',
    value: `GPA: ${gpa}`,
    boost: gpa >= 3.5 ? 15 : gpa >= 3.0 ? 10 : 5
  });
  
  // Athletic recognition
  const recognitions = {
    star: ['All-Conference', 'Team Captain', 'National Rankings'],
    rising: ['Honor Roll', 'Team Leader', 'Conference Recognition'],
    solid: ['Academic All-Conference', 'Starter', 'Team Contributor'],
    developing: ['Dean\'s List', 'Rising Talent', 'Practice Player of Week']
  };
  
  multipliers.push({
    factor: 'Athletic Recognition',
    value: recognitions[archetype].slice(0, 2).join(', '),
    boost: archetype === 'star' ? 30 : archetype === 'rising' ? 20 : archetype === 'solid' ? 15 : 10
  });
  
  // Team performance
  multipliers.push({
    factor: 'Team Performance',
    value: 'Conference contender',
    boost: 25
  });
  
  // Conference
  multipliers.push({
    factor: 'Conference Reputation',
    value: 'ACC Conference',
    boost: 12
  });
  
  // Year bonus
  if (year === 'Sr') {
    multipliers.push({
      factor: 'Senior Leadership',
      value: 'Final season boost',
      boost: 10
    });
  }
  
  return multipliers;
};

// Main player generator
export const generatePlayerData = (name, position, year, archetype) => {
  const archetypeConfig = archetypes[archetype];
  
  // Base stats
  const slamScore = Math.round(
    archetypeConfig.slamScoreRange[0] + 
    Math.random() * (archetypeConfig.slamScoreRange[1] - archetypeConfig.slamScoreRange[0])
  );
  
  const nilValue = Math.round(
    archetypeConfig.nilValueRange[0] + 
    Math.random() * (archetypeConfig.nilValueRange[1] - archetypeConfig.nilValueRange[0])
  );
  
  const socialFollowers = Math.round(
    archetypeConfig.socialFollowersRange[0] + 
    Math.random() * (archetypeConfig.socialFollowersRange[1] - archetypeConfig.socialFollowersRange[0])
  );
  
  const marketRank = archetype === 'star' ? Math.floor(Math.random() * 10) + 1 :
                     archetype === 'rising' ? Math.floor(Math.random() * 20) + 10 :
                     archetype === 'solid' ? Math.floor(Math.random() * 30) + 20 :
                     Math.floor(Math.random() * 40) + 30;
  
  const gpa = 2.8 + Math.random() * 1.2;
  
  // Generate detailed data
  const socialMediaData = generateSocialMedia(socialFollowers, archetype);
  const brandOpportunities = generateBrandPartnerships(archetype, slamScore);
  const performanceMultipliers = generateMultipliers(archetype, year, gpa.toFixed(2));
  const performanceMetrics = generatePerformanceMetrics(position, archetype);
  
  // Calculate projections
  const totalBoost = performanceMultipliers.reduce((sum, m) => sum + m.boost, 0);
  const baseProjection = nilValue;
  const realisticProjection = Math.round(baseProjection * (1 + totalBoost / 100));
  const optimisticProjection = Math.round(realisticProjection * 1.25);
  const conservativeProjection = Math.round(realisticProjection * 0.85);
  
  return {
    // Basic info
    name,
    position,
    year,
    archetype,
    slamScore,
    nilValue,
    socialFollowers,
    marketRank,
    trend: archetypeConfig.growthRate === 'high' ? 'up' : 
           archetypeConfig.growthRate === 'medium' ? 'stable' : 'stable',
    
    // Social media
    socialMediaData,
    
    // Brand partnerships
    brandOpportunities,
    
    // Performance multipliers
    performanceMultipliers,
    totalBoost,
    
    // Projections
    earningProjections: {
      optimistic: optimisticProjection,
      realistic: realisticProjection,
      base: conservativeProjection
    },
    
    // Performance metrics
    performanceMetrics,
    
    // Additional details
    hometown: generateHometown(),
    major: generateMajor(),
    gpa: gpa.toFixed(2),
    activeDeals: Math.floor(Math.random() * 5) + (archetype === 'star' ? 3 : 1),
    platformConnections: {
      opendorse: archetype !== 'developing',
      postgame: archetype === 'star' || archetype === 'rising',
      sanil: Math.random() > 0.5,
      nilsu: true
    }
  };
};

// Helper functions
const generateHometown = () => {
  const cities = [
    'Richmond, VA', 'Virginia Beach, VA', 'Atlanta, GA', 'Charlotte, NC',
    'Nashville, TN', 'Tampa, FL', 'Houston, TX', 'Phoenix, AZ',
    'San Diego, CA', 'Portland, OR', 'Chicago, IL', 'Boston, MA'
  ];
  return cities[Math.floor(Math.random() * cities.length)];
};

const generateMajor = () => {
  const majors = [
    'Business Administration', 'Sports Management', 'Communications',
    'Economics', 'Psychology', 'Kinesiology', 'Marketing',
    'Computer Science', 'Engineering', 'Biology', 'Finance'
  ];
  return majors[Math.floor(Math.random() * majors.length)];
};

// Full roster data
export const rosterData = {
  'Blake Barker': generatePlayerData('Blake Barker', 'P', 'Jr', 'star'),
  'Marcus Johnson': generatePlayerData('Marcus Johnson', 'SS', 'Sr', 'rising'),
  'Tyler Chen': generatePlayerData('Tyler Chen', 'OF', 'So', 'rising'),
  'Jake Martinez': generatePlayerData('Jake Martinez', 'C', 'Jr', 'solid'),
  'Ryan O\'Brien': generatePlayerData('Ryan O\'Brien', '1B', 'Sr', 'star'),
  'Ethan Williams': generatePlayerData('Ethan Williams', 'OF', 'Fr', 'developing'),
  'Connor Davis': generatePlayerData('Connor Davis', 'P', 'So', 'rising'),
  'Alex Thompson': generatePlayerData('Alex Thompson', '2B', 'Jr', 'solid'),
  'Jordan Lee': generatePlayerData('Jordan Lee', '3B', 'Sr', 'solid'),
  'Michael Rodriguez': generatePlayerData('Michael Rodriguez', 'P', 'Fr', 'developing'),
  'Chris Anderson': generatePlayerData('Chris Anderson', 'OF', 'Jr', 'rising'),
  'Matt Wilson': generatePlayerData('Matt Wilson', 'C', 'So', 'solid'),
  'Nick Garcia': generatePlayerData('Nick Garcia', 'SS', 'Fr', 'developing'),
  'David Kim': generatePlayerData('David Kim', 'P', 'Jr', 'solid'),
  'James Taylor': generatePlayerData('James Taylor', '1B', 'So', 'rising')
};

export default rosterData;